import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, signal, SimpleChanges, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BinaryDecisionViewModel, ComponentType, PromptComponent } from '@k12/models';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule],

  selector: 'lib-pc-binary-decision',
  styleUrl: './pc-binary-decision.component.scss',
  templateUrl: './pc-binary-decision.component.html'
})
export class PcBinaryDecisionComponent implements OnInit, OnChanges {
  @Input() allowPointerEvents!: boolean;
  @Input() formGroup: FormGroup = new FormGroup({});
  @Input() configJson: PromptComponent = {
    componentType: ComponentType.BinaryDecision,
    header: undefined,
    inputType: 'binary_decision',
    instructions: undefined,
    label: '',
    order: 0,
    inputs: [
      {
        inputType: 'radio',
        instructions: '',
        order: 0,
        label: '',
        dataType: 'text',
        isRequired: true,
        options: [
          {
            order: 0,
            label: 'Yes',
            value: 'yes',
            subLabel: '',
            dataType: 'text'
          },
          {
            order: 1,
            label: 'No',
            value: 'no',
            subLabel: '',
            dataType: 'text'
          }
        ]
      }
    ]
  };
  @Input() isYesNo: WritableSignal<boolean> = signal(false);

  @Output() cbConfig = new EventEmitter();
  @Output() decisionEvent = new EventEmitter();

  public selectedDecision!: boolean | number;
  public vm!: BinaryDecisionViewModel;

  public ngOnInit(): void {
    this.vm = {
      noIcon: 'fa fa-circle-xmark',
      noLabel: 'No',
      yesIcon: 'fa fa-circle-check',
      yesLabel: 'Yes'
    };
    this.cbConfig.emit(this.configJson);
    this.isYesNo.set(this.configJson.componentType === ComponentType.YesNoDecision);
    this.syncSelectedDecisionFromConfig();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ((changes['configJson'] && !changes['configJson'].firstChange) || changes['formGroup']) {
      this.isYesNo.set(this.configJson.componentType === ComponentType.YesNoDecision);
      this.syncSelectedDecisionFromConfig();
    }
  }

  public decisionButtonClick(event: boolean | number): void {
    // TODO (future): Convert string to boolean when BE is ready
    let emitValue: boolean | string | number = event;
    this.selectedDecision = emitValue;

    if (typeof event === 'number') {
      emitValue = event === 0 ? 'true' : 'false';
      this.selectedDecision = event;
    }

    this.decisionEvent.emit({
      formName: this.configJson.id ?? '',
      control: { [this.configJson.inputs[0].id ?? '']: emitValue.toString() }
    });
  }

  /**
   *  Synchronizes the selected decision state with the current configuration and form group values.
   *  This method checks the form group for the value of the binary decision input and updates the selectedDecision property accordingly.
   *  If the value is not found or is invalid, it defaults to -1 (no selection).
   *
   *  The method handles different data types (boolean, string) and normalizes them to determine the selected decision index.
   *
   *  @remarks
   *  This method is called during initialization and whenever the configuration or form group changes.
   *  @returns void
   */
  private syncSelectedDecisionFromConfig(): void {
    const input = this.configJson.inputs?.[0];
    const inputId = input?.id;
    const rawValue = (inputId ? this.formGroup?.get(inputId)?.value : undefined) ?? input?.value;

    if (rawValue === undefined || rawValue === null || rawValue === '') {
      this.selectedDecision = -1;
      return;
    }

    if (typeof rawValue === 'boolean') {
      this.selectedDecision = rawValue ? 0 : 1;
      return;
    }

    const normalized = String(rawValue).trim().toLowerCase();
    if (normalized === 'true' || normalized === 'yes') {
      this.selectedDecision = 0;
      return;
    }
    if (normalized === 'false' || normalized === 'no') {
      this.selectedDecision = 1;
      return;
    }

    const optionIndex = (input?.options ?? []).findIndex(
      (option) => String(option.value ?? '').trim().toLowerCase() === normalized
    );
    this.selectedDecision = optionIndex >= 0 ? optionIndex : -1;
  }
}
